import { useMemo } from 'react'
import { IconArrowUp, IconCopy, IconEdit, IconHelp, IconTrash } from '@tabler/icons-react'
import { MantineReactTable, MRT_ColumnDef, useMantineReactTable } from 'mantine-react-table'
import { UUIDTypes } from 'uuid'
import { Button, Group, NumberFormatter, Tooltip, Text, Stack } from '@mantine/core'
import { tableDefaultProps } from '@shared/components/table/table'
import { NoDataMessage } from '@shared/components/table/table-no-data-message'
import { SpeciLineDtoInput } from '@loom-api-types'
import { SpeciLine } from '@shared/components/speci/speci-types'
import classes from './speci-lines-table.module.css'
import { BalesList } from '@shared/components/speci/bales-list/bales-list'
import { SpeciLinesInformation } from '@shared/components/speci/speci-helpers/speci-helpers'

export const MOVE_FOCUS_DELAY_MS = 400

interface SpeciLinesTableProps {
  data: SpeciLineDtoInput[]
  sumOfTotalBales?: number
  noDataMessage?: string
  isLoading?: boolean
  disableDuplicate?: boolean
  lineEditorFirstFieldRef?: React.RefObject<HTMLInputElement>
  activeRowId?: UUIDTypes
  isDuplicating?: boolean
  updateSpeciLine?: (id: UUIDTypes | undefined, action: SpeciLineAction) => void
  speciLinesInformation?: SpeciLinesInformation
}

export enum SpeciLineAction {
  EDIT = 'edit',
  DELETE = 'delete',
  DUPLICATE = 'duplicate',
}

export const SpeciLinesTable = ({
  data = [],
  sumOfTotalBales,
  noDataMessage = 'No lines are defined on this speci',
  disableDuplicate = false,
  lineEditorFirstFieldRef,
  activeRowId,
  isDuplicating,
  isLoading,
  updateSpeciLine = () => {},
  speciLinesInformation,
}: SpeciLinesTableProps) => {
  const columns = useMemo<MRT_ColumnDef<SpeciLine>[]>(
    () => [
      {
        id: 'number',
        accessorKey: 'number',
        header: 'Line number',
        Footer: () => <>Total</>,
        sortingFn: (a, b, columnId) => {
          const valueA = a.getValue<string | null>(columnId)
          const valueB = b.getValue<string | null>(columnId)

          if (valueA === valueB) return 0
          if (!valueA) return 1
          if (!valueB) return -1

          return valueA.localeCompare(valueB, undefined, { numeric: true })
        },
      },
      {
        id: 'description',
        accessorKey: 'description',
        header: 'Line description',
        enableSorting: false,
      },
      {
        id: 'traditionalDescription',
        accessorKey: 'traditionalDescription',
        header: 'Tradition description',
        enableSorting: false,
      },
      {
        id: 'baleNumbers',
        accessorKey: 'baleNumbers',
        header: 'Bales',
        enableSorting: false,
        minSize: 200,
        Cell: ({ cell }) => (
          <BalesList
            baleNumbers={cell.getValue<Array<number>>()}
            duplicates={speciLinesInformation?.duplicates}
          />
        ),
      },
      {
        id: 'baleNumberCount',
        header: 'Bale Count',
        enableSorting: false,
        accessorFn: (row) => row.baleNumbers.length,
        Footer: () => <NumberFormatter value={sumOfTotalBales} thousandSeparator />,
      },
      {
        id: 'instructions',
        accessorKey: 'instructions',
        header: 'Instruction',
        enableSorting: false,
        Cell: ({ row }) => (
          <Group justify='space-between'>
            <div>{row.original.instructions}</div>
            {row.original.originalInstructions &&
              row.original.originalInstructions !== row.original.instructions && (
                <Tooltip
                  label={`Original grower instruction: ${row.original.originalInstructions}`}
                  events={{ hover: true, focus: true, touch: false }}
                >
                  <IconHelp
                    size={18}
                    color='var(--mantine-color-gray-4)'
                    aria-label={`Original grower instruction: ${row.original.originalInstructions}`}
                  />
                </Tooltip>
              )}
          </Group>
        ),
      },
    ],
    [sumOfTotalBales, speciLinesInformation?.duplicates]
  )

  /*
   * Move focus to the first field of the line editor. Timeout required to
   * ensure value updated before focus is moved to the input.
   */
  const moveFocusToLineEditor = () => {
    if (lineEditorFirstFieldRef?.current) {
      lineEditorFirstFieldRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
      // Wait for scroll animation to finish before focusing
      setTimeout(() => {
        lineEditorFirstFieldRef.current?.focus()
      }, MOVE_FOCUS_DELAY_MS)
    }
  }

  const handleEdit = (rowId: UUIDTypes | undefined, action: SpeciLineAction) => {
    if (action === SpeciLineAction.EDIT) {
      updateSpeciLine(rowId, SpeciLineAction.EDIT)
    } else if (action === SpeciLineAction.DUPLICATE) {
      updateSpeciLine(rowId, SpeciLineAction.DUPLICATE)
    }

    moveFocusToLineEditor()
  }

  const table = useMantineReactTable({
    ...tableDefaultProps,
    columns,
    data,
    state: {
      isLoading,
    },
    enableSorting: true,
    initialState: {
      ...tableDefaultProps.initialState,
      sorting: [
        {
          id: 'number', // Default sort by line number
          desc: false,
        },
      ],
    },
    renderEmptyRowsFallback: () => <NoDataMessage noDataMessage={noDataMessage} />,
    mantineTableProps: {
      ...tableDefaultProps.mantineTableProps,
      'aria-label': 'Speci lines',
    },
    mantineTableBodyRowProps: ({ row }) => ({
      className: row.original.id === activeRowId ? classes.activeRow : '',
    }),
    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    enableRowActions: true,
    positionActionsColumn: 'last',
    renderRowActions: ({ row }) => {
      // If currently editing, replace actions with message/scroll up button
      const rowContents =
        activeRowId === row.original.id ? (
          <>
            <Text size='sm'>Currently {isDuplicating ? 'duplicating' : 'editing'} line.</Text>
            <Button
              variant='outline'
              size='xs'
              leftSection={<IconArrowUp />}
              onClick={moveFocusToLineEditor}
            >
              Scroll to editor
            </Button>
          </>
        ) : (
          <>
            <Button
              size='xs'
              variant='outline'
              onClick={() => handleEdit(row.original.id, SpeciLineAction.EDIT)}
              leftSection={<IconEdit />}
            >
              Edit
            </Button>
            {!disableDuplicate && (
              <Button
                size='xs'
                variant='outline'
                onClick={() => handleEdit(row.original.id, SpeciLineAction.DUPLICATE)}
                leftSection={<IconCopy />}
              >
                Duplicate
              </Button>
            )}
            <Button
              size='xs'
              color='red'
              variant='outline'
              onClick={() => updateSpeciLine(row.original.id, SpeciLineAction.DELETE)}
              leftSection={<IconTrash />}
            >
              Delete
            </Button>
          </>
        )
      return <Stack gap='xs'>{rowContents}</Stack>
    },
  })

  return <MantineReactTable table={table} key={sumOfTotalBales} />
}
